import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  useMemo,
  memo,
  useRef,
  Fragment,
  useCallback,
  cloneElement,
  isValidElement,
} from 'react';
import { useListContext, useResourceContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { getResultsSx } from 'components/Search/styles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { SearchTextField, useSearchTextField } from './SearchTextField';
import SortDropdown, { SortOption } from '../SortDropdown';
import { useSearch } from '../../SearchContextProvider';
import { AdvancedSearchFiltersRow } from './SearchAdvancedFilters';
import { getSortOptions } from '../../functions';
import { useEditorialAiSearchState } from './components/AISearch/useEditorialAiSearchState';
import type { EditorialPostSearchContentProps } from './components/Editorial/EditorialPostSearchContent';

interface SearchAdvancedTopBarProps {
  /** For wire search, allow using 'raw_query' param instead of 'query' */
  useRawQuery?: boolean;
  hideSearchBar?: boolean;
  /** left of searchbar (eg grid/list view btns) */
  preSearchComponent?: React.ReactNode;
  /** inside searchbar (eg wire channels dropdown) */
  searchDropdownComponent?: React.ReactNode;
  /** right of searchbar (eg saved search star) */
  postSearchComponent?: React.ReactNode;
  /** end of container (eg batch edit search btn) */
  endSearchComponent?: React.ReactNode;
  /**
   * Advanced filters row below search bar
   * passed to <SearchAdvancedFiltersRow />
   */
  advancedFilterFieldsComponent?: React.ReactNode;
  /**
   * Advanced search popover fields body component
   * passed to <SearchAdvancedPopover />
   * if null, will also hide the advanced search btn
   */
  advancedPopoverFieldsComponent?: React.ReactNode;
  /**
   * multi site user search specific props
   */
  expandPostSearchComponent?: boolean;
  showSubmitBtn?: boolean;
  onSubmitResetSearchState?: () => void;
  lastSubmittedSearchRef?: React.MutableRefObject<string | null>;
  effectiveDomains?: string[];
}

export const SearchAdvancedTopBar = memo(
  ({
    useRawQuery = false,
    hideSearchBar = false,
    preSearchComponent = null,
    searchDropdownComponent = null,
    postSearchComponent = null,
    endSearchComponent = null,
    advancedPopoverFieldsComponent = null,
    advancedFilterFieldsComponent = null,
    // --- multisite user search ---
    expandPostSearchComponent = false,
    showSubmitBtn = false,
    lastSubmittedSearchRef,
    effectiveDomains,
    onSubmitResetSearchState,
  }: SearchAdvancedTopBarProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();
    const resource = useResourceContext();
    const { sessionConfig, userInfo } = useCosmos();
    const { data, total = 0 } = useListContext();
    const {
      view,
      resourceContexts,
      updateQueryParams,
      isModalSearch,
      updateFilters,
      validListFilter,
    } = useSearch();

    const {
      setAiSearchEnabledInStorage,
      hasAiSearchPrerequisites,
      isEditorialAndActive,
    } = useEditorialAiSearchState({
      resource,
      sessionConfig,
      userInfo,
    });

    const textInputRef = useRef<HTMLInputElement>(null);
    const focusSearchInput = useCallback(() => {
      textInputRef.current?.focus();
    }, []);

    const { rawInput, setRawInput, setTextValue, paramTarget, textFieldValue } =
      useSearchTextField({
        aiSearchEnabled: isEditorialAndActive,
        useRawQuery: useRawQuery,
        showSubmitBtn: showSubmitBtn,
        textInputRef: textInputRef,
      });

    // Handle activation of AI search, updating filters or query params accordingly
    const handleAiSearchActivate = useCallback(() => {
      if (isModalSearch && updateFilters) {
        const queryValue = validListFilter?.query ?? '';
        updateFilters({
          ...validListFilter,
          semantic: queryValue,
          query: '',
          sort: '',
          dir: '',
        });
        setRawInput(queryValue);
        return;
      }

      updateQueryParams(
        (prev) => {
          const text = prev.get('query') ?? '';
          prev.delete('query');
          prev.delete('sort');
          prev.delete('dir');
          if (text) {
            prev.set('semantic', text);
          }
          return prev;
        },
        { replace: true },
      );
    }, [isModalSearch, setRawInput, updateFilters, updateQueryParams, validListFilter]);

    // Handle deactivation of AI search, reverting filters or query params accordingly
    const handleAiSearchDeactivate = useCallback(() => {
      if (isModalSearch && updateFilters) {
        const semanticValue = validListFilter?.semantic ?? '';
        updateFilters({
          ...validListFilter,
          query: semanticValue,
          semantic: '',
          sort: '',
          dir: '',
        });
        setRawInput(semanticValue);
        return;
      }

      updateQueryParams(
        (prev) => {
          const text = prev.get('semantic') ?? '';
          prev.delete('semantic');
          if (text) {
            prev.set('query', text);
          }
          return prev;
        },
        { replace: true },
      );
    }, [isModalSearch, setRawInput, updateFilters, updateQueryParams, validListFilter]);

    const dataLength = data?.length || 0;
    const totalResults = Number(
      (total || dataLength) && (total > 0 || dataLength)
        ? total < dataLength
          ? dataLength
          : total
        : 0,
    );

    // get sorting options
    const sortOptions = getSortOptions({
      resource,
      resourceContexts,
    });

    // card view sort button
    const showSortButton = useMemo(() => {
      const show = view === 'card' && Boolean(sortOptions?.length);
      return show;
    }, [view, sortOptions]);

    return (
      <Box
        id="inputgrid"
        sx={{
          container: 'inputgrid / inline-size',
          display: 'grid',
          gridTemplateAreas: `'inputgridtop''inputgridbottom'`,
          gridTemplateRows: 'auto auto',
          gridTemplateColumns: '1fr',
          overflow: 'hidden',
          width: '100%',
          minHeight: 60,
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: 'center',
        }}
      >
        {/* Search Inputs Area */}
        <Box
          id="inputgridtop"
          sx={{
            gridArea: 'inputgridtop',
            container: 'inputgridtop / inline-size',
            overflowX: 'auto',
            overflowY: 'hidden',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            p: 1,
          }}
        >
          <Stack
            id="searchinputs-top-inputs-container"
            direction="row"
            sx={{
              alignItems: 'center',
              gap: isMobile ? 0.5 : 1,
              flexGrow: 1,
              minWidth: 0,
              flexWrap: 'nowrap',
              flexShrink: 1,
            }}
          >
            {/* Pre Search Input Component */}
            {preSearchComponent}

            {/* Conditionally render the search bar */}
            {!hideSearchBar && (
              <Fragment>
                <SearchTextField
                  rawInput={rawInput}
                  setRawInput={setRawInput}
                  paramTarget={paramTarget}
                  setTextValue={setTextValue}
                  textFieldValue={textFieldValue}
                  aiSearchEnabled={isEditorialAndActive}
                  useRawQuery={useRawQuery}
                  onSubmitResetSearchState={onSubmitResetSearchState}
                  effectiveDomains={effectiveDomains}
                  showSubmitBtn={showSubmitBtn}
                  lastSubmittedSearchRef={lastSubmittedSearchRef}
                  textInputRef={textInputRef}
                  searchDropdownComponent={searchDropdownComponent}
                  advancedPopoverFieldsComponent={advancedPopoverFieldsComponent}
                />
              </Fragment>
            )}

            {/* Post Search Input Component */}
            {postSearchComponent && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: expandPostSearchComponent ? '1 1 0' : '0 0 auto',
                  minWidth: 0,
                  width: expandPostSearchComponent ? '100%' : undefined,
                }}
                data-testid="topbar-post-search-component-container"
              >
                {isValidElement(postSearchComponent)
                  ? cloneElement(postSearchComponent, {
                      onRequestFocusSearchInput: focusSearchInput,
                      aiSearchActive: isEditorialAndActive,
                      showAiSearchToggle: hasAiSearchPrerequisites,
                      setAiSearchActive: setAiSearchEnabledInStorage,
                      onAiSearchActivate: handleAiSearchActivate,
                      onAiSearchDeactivate: handleAiSearchDeactivate,
                    } as Partial<EditorialPostSearchContentProps>)
                  : postSearchComponent}
              </Box>
            )}
          </Stack>

          <Stack
            id="searchinputs-top-end-group"
            direction="row"
            sx={{
              alignItems: 'center',
              flexShrink: 0,
              gap: isMobile ? 0 : 1,
            }}
          >
            {/* End Top Bar Component */}
            <Box data-testid="topbar-results-and-sort-container">
              {!isMobile && (
                <Typography
                  sx={getResultsSx}
                  data-testid="topbar-results-text"
                >
                  {totalResults && totalResults > 0
                    ? totalResults === 1
                      ? t('blox.general.count_results_one', {
                          count: Number(totalResults),
                        })
                      : t('blox.general.count_results', { count: Number(totalResults) })
                    : t('blox.general.no_results')}
                </Typography>
              )}

              {/* Render SortDropdown based on check */}
              {showSortButton && (
                <SortDropdown sortOptions={sortOptions as SortOption[]} />
              )}
            </Box>
            {endSearchComponent}
          </Stack>
        </Box>

        {/* Active Filters Row */}
        <Box
          id="inputgridbottom"
          sx={{
            gridArea: 'inputgridbottom',
            container: 'inputgridbottom / inline-size',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'auto', // allow horizontal scroll on mobile
          }}
        >
          {advancedFilterFieldsComponent && (
            <AdvancedSearchFiltersRow
              advancedFilterFieldsComponent={advancedFilterFieldsComponent}
            />
          )}
        </Box>
      </Box>
    );
  },
);
SearchAdvancedTopBar.displayName = 'SearchAdvancedTopBar';
